import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  GraduationCap,
  Wallet,
  HeadphonesIcon,
  Users,
  ClipboardList,
  BarChart2,
  Users2,
  Calendar,
  ChevronLeft,
  UserCircle2,
  CalendarClock,
  Zap,
  X,
  LogOut,
  Settings2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../ui/logo.png';
import useAuth from '../../../hooks/useAuth';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/' },
  { icon: Users, label: 'Students', href: '/student' },
  { icon: UserCircle2, label: 'Parents', href: '/parents' },
  { icon: Users2, label: 'Team', href: '/team' },
  { icon: Calendar, label: 'Events', href: '/events' },
  { icon: GraduationCap, label: 'Academics', href: '/academics' },
  { icon: Wallet, label: 'Accounts', href: '/accounts' },
  { icon: ClipboardList, label: 'Attendance', href: '/attendance' },
  { icon: BarChart2, label: 'Analytics', href: '/analytics' },
  { icon: HeadphonesIcon, label: 'Support', href: '/support' },
];

const staffMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/' },
  { icon: Users, label: 'Students', href: '/student' },
  { icon: UserCircle2, label: 'Parents', href: '/parents' },
  { icon: Calendar, label: 'Events', href: '/events' },
  { icon: GraduationCap, label: 'Academics', href: '/academics' },
  { icon: HeadphonesIcon, label: 'Support', href: '/support' },
];

export default function Sidebar({ isMobile, isOpen, setIsOpen }) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const { user, logout } = useAuth();

  // Check if current path matches menu item path or if it's a subpath
  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  if (user.Role.baseRole === 'superadmin') {
    // If on mobile, render the sliding sidebar overlay
    if (isMobile) {
      return (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-xl border-r border-gray-100"
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: 'spring', stiffness: 300, damping: 35 }}
            >
              {/* Mobile Sidebar Header with Close Button */}
              <div className="flex h-20 items-center justify-between px-4">
                <Link
                  to="/"
                  className="flex items-center gap-2"
                  onClick={() => setIsOpen(false)}
                >
                  <img
                    src={logo}
                    alt="Logo"
                    className="h-10 w-10 object-contain"
                  />
                  <span className="text-lg font-semibold">Campus Manager</span>
                </Link>
                <button
                  className="p-2 rounded-full hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close sidebar"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Mobile Navigation */}
              <nav className="mt-2 px-4 pb-24 overflow-y-auto max-h-[calc(100vh-5rem)]">
                <ul className="space-y-1">
                  {menuItems.map((item) => {
                    const isActiveRoute = isActive(item.href);
                    return (
                      <li key={item.label}>
                        <Link
                          to={item.href}
                          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors
                          ${
                            isActiveRoute
                              ? 'bg-purple-50 text-purple-700'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
                          }`}
                          onClick={() => setIsOpen(false)}
                        >
                          <item.icon
                            className={`h-5 w-5 flex-shrink-0 ${
                              isActiveRoute
                                ? 'text-purple-700'
                                : 'text-gray-500'
                            }`}
                          />
                          <span>{item.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      );
    }

    // Desktop sidebar with collapse functionality
    return (
      <motion.div
        className="relative h-screen border-r border-gray-100 bg-white flex flex-col justify-between"
        initial={false}
        animate={{
          width: isCollapsed ? 88 : 280,
          transition: { duration: 0.3, ease: 'easeInOut' },
        }}
      >
        {/* Toggle Button - Desktop only */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-20 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition-transform hover:scale-105 focus:outline-none"
        >
          <motion.div
            initial={false}
            animate={{ rotate: isCollapsed ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </motion.div>
        </button>

        <div className="flex-[0.7]">
          {/* Logo Section */}
          <div className="flex h-20 items-center px-6">
            <Link to="/" className="flex items-center gap-4">
              <img src={logo} alt="Logo" className="h-32 w-32 object-contain" />
              {/* <AnimatePresence>
            {!isCollapsed && (
              <motion.span
                className="text-xl font-semibold"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                Campus Manager
              </motion.span>
            )}
          </AnimatePresence> */}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="mt-6 overflow-y-auto max-h-[calc(100vh-5rem)]">
            <ul>
              {menuItems.map((item) => {
                const isActiveRoute = isActive(item.href);
                return (
                  <li key={item.label} className="relative">
                    <Link
                      to={item.href}
                      className={`group relative flex items-center gap-4 px-4 py-2 text-base font-medium transition-all duration-200
                    ${
                      isActiveRoute
                        ? 'bg-purple-50 text-purple-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
                    }`}
                    >
                      <item.icon
                        className={`h-5 w-5 flex-shrink-0 transition-colors
                      ${
                        isActiveRoute
                          ? 'text-purple-700'
                          : 'text-gray-500 group-hover:text-purple-600'
                      }`}
                        strokeWidth="2px"
                      />
                      <AnimatePresence>
                        {!isCollapsed && (
                          <motion.span
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                            className="whitespace-nowrap tracking-wide"
                          >
                            {item.label}
                          </motion.span>
                        )}
                      </AnimatePresence>

                      {/* Tooltip for collapsed state */}
                      {isCollapsed && (
                        <div className="absolute left-full top-1/2 z-50 ml-4 -translate-y-1/2 hidden rounded-lg bg-gray-900/95 backdrop-blur-sm px-3 py-2 text-sm text-white shadow-lg group-hover:block">
                          {item.label}
                        </div>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <div className=" flex-[0.1] border-t border-gray-200">
          <div
            className={`group relative cursor-pointer flex items-center gap-4 px-4 py-2 text-base font-medium transition-all duration-200
                    ${
                      isActive('/support')
                        ? 'bg-purple-50 text-purple-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
                    }`}
            onClick={() => logout()}
          >
            <LogOut
              className={`h-5 w-5 flex-shrink-0 transition-colors
                      ${
                        isActive('/support')
                          ? 'text-purple-700'
                          : 'text-gray-500 group-hover:text-purple-600'
                      }`}
              strokeWidth="2px"
            />
            <AnimatePresence>
              {!isCollapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="whitespace-nowrap tracking-wide"
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>

            {/* Tooltip for collapsed state */}
            {isCollapsed && (
              <div className="absolute left-full top-1/2 z-50 ml-4 -translate-y-1/2 hidden rounded-lg bg-gray-900/95 backdrop-blur-sm px-3 py-2 text-sm text-white shadow-lg group-hover:block">
                Logout
              </div>
            )}
          </div>
          <Link
            to={'/org/settings'}
            className={`group relative flex items-center gap-4 px-4 py-2 text-base font-medium transition-all duration-200
                    ${
                      isActive('/support')
                        ? 'bg-purple-50 text-purple-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
                    }`}
          >
            <Settings2
              className={`h-5 w-5 flex-shrink-0 transition-colors
                      ${
                        isActive('/support')
                          ? 'text-purple-700'
                          : 'text-gray-500 group-hover:text-purple-600'
                      }`}
              strokeWidth="2px"
            />
            <AnimatePresence>
              {!isCollapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="whitespace-nowrap tracking-wide"
                >
                  School Settings
                </motion.span>
              )}
            </AnimatePresence>

            {/* Tooltip for collapsed state */}
            {isCollapsed && (
              <div className="absolute left-full top-1/2 z-50 ml-4 -translate-y-1/2 hidden rounded-lg bg-gray-900/95 backdrop-blur-sm px-3 py-2 text-sm text-white shadow-lg group-hover:block">
                School Settings
              </div>
            )}
          </Link>
          <div
            className={`flex ${
              isCollapsed ? 'flex-col items-start gap-y-2 pl-4' : 'flex-row'
            } justify-evenly py-2`}
          >
            <div className="w-6 h-6 bg-gray-500 rounded-full" />
            <div className="w-6 h-6 bg-gray-500 rounded-full" />
            <div className="flex gap-x-3">
              <p>{user.firstName}</p>
              <p>{user.lastName}</p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Normal Staff User
  // If on mobile, render the sliding sidebar overlay
  if (isMobile) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-xl border-r border-gray-100"
            initial={{ x: -320 }}
            animate={{ x: 0 }}
            exit={{ x: -320 }}
            transition={{ type: 'spring', stiffness: 300, damping: 35 }}
          >
            {/* Mobile Sidebar Header with Close Button */}
            <div className="flex h-20 items-center justify-between px-4">
              <Link
                to="/"
                className="flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <img
                  src={logo}
                  alt="Logo"
                  className="h-10 w-10 object-contain"
                />
                <span className="text-lg font-semibold">Campus Manager</span>
              </Link>
              <button
                className="p-2 rounded-full hover:bg-gray-100"
                onClick={() => setIsOpen(false)}
                aria-label="Close sidebar"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Mobile Navigation */}
            <nav className="mt-2 px-4 pb-24 overflow-y-auto max-h-[calc(100vh-5rem)]">
              <ul className="space-y-1">
                {staffMenuItems.map((item) => {
                  const isActiveRoute = isActive(item.href);
                  return (
                    <li key={item.label}>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors
                          ${
                            isActiveRoute
                              ? 'bg-purple-50 text-purple-700'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
                          }`}
                        onClick={() => setIsOpen(false)}
                      >
                        <item.icon
                          className={`h-5 w-5 flex-shrink-0 ${
                            isActiveRoute ? 'text-purple-700' : 'text-gray-500'
                          }`}
                        />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Desktop sidebar with collapse functionality
  return (
    <motion.div
      className="relative h-screen border-r border-gray-100 bg-white"
      initial={false}
      animate={{
        width: isCollapsed ? 88 : 280,
        transition: { duration: 0.3, ease: 'easeInOut' },
      }}
    >
      {/* Logo Section */}
      <div className="flex h-20 items-center px-6">
        <Link to="/" className="flex items-center gap-4">
          <img src={logo} alt="Logo" className="h-32 w-32 object-contain" />
          {/* <AnimatePresence>
            {!isCollapsed && (
              <motion.span
                className="text-xl font-semibold"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                Campus Manager
              </motion.span>
            )}
          </AnimatePresence> */}
        </Link>
      </div>

      {/* Toggle Button - Desktop only */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-20 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition-transform hover:scale-105 focus:outline-none"
      >
        <motion.div
          initial={false}
          animate={{ rotate: isCollapsed ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </motion.div>
      </button>

      {/* Desktop Navigation */}
      <nav className="mt-6 px-4 overflow-y-auto max-h-[calc(100vh-5rem)]">
        <ul className="space-y-2">
          {staffMenuItems.map((item) => {
            const isActiveRoute = isActive(item.href);
            return (
              <li key={item.label} className="relative">
                <Link
                  to={item.href}
                  className={`group relative flex items-center gap-4 rounded-lg px-4 py-3 text-base font-medium transition-all duration-200
                    ${
                      isActiveRoute
                        ? 'bg-purple-50 text-purple-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
                    }`}
                >
                  <item.icon
                    className={`h-6 w-6 flex-shrink-0 transition-colors
                      ${
                        isActiveRoute
                          ? 'text-purple-700'
                          : 'text-gray-500 group-hover:text-purple-600'
                      }`}
                  />
                  <AnimatePresence>
                    {!isCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        className="whitespace-nowrap"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>

                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="absolute left-full top-1/2 z-50 ml-4 -translate-y-1/2 hidden rounded-lg bg-gray-900/95 backdrop-blur-sm px-3 py-2 text-sm text-white shadow-lg group-hover:block">
                      {item.label}
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </motion.div>
  );
}
